import React, {
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { UserContext } from "../../UserContext";
import "./styles.css";
import Dropdown from "react-bootstrap/Dropdown";
import { Card, Table, Pagination, Row, Col } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import {
  FaSearch,
  FaFilter,
  FaTimes,
  FaMoneyBillWave,
  FaSortAmountDown,
  FaSortAmountUp,
} from "react-icons/fa";
import toast from "react-hot-toast";

// Import custom components
import TransactionCard from "./components/TransactionCard";
import FinancialSummary from "./components/FinancialSummary";
import DateRangeSelector from "./components/DateRangeSelector";
import EmptyState from "./components/EmptyState";
import DeleteTransactionModal from "./components/DeleteTransactionModal";

// Import assets
import payments from "../../assets/Expenses & Sales/payments.png";
import expenses from "../../assets/Expenses & Sales/expenses.png";
import incomes from "../../assets/Expenses & Sales/incomes.png";
import dateRangeImg from "../../assets/Expenses & Sales/date_range.png";
import totalExpense from "../../assets/Expenses & Sales/total_expense.png";
import totalIncome from "../../assets/Expenses & Sales/total_income.png";

const Payments = () => {
  const [t, i18n] = useTranslation();
  const navigate = useNavigate();
  const { userData, token } = useContext(UserContext);

  // Core state
  const [paymentsData, setPaymentsData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [shouldRefetch, setShouldRefetch] = useState(false);

  // View and filter state
  const [activeView, setActiveView] = useState("overview"); // overview | summary
  const [dateRange, setDateRange] = useState("today");
  const [summaryDateRange, setSummaryDateRange] = useState("week"); // Separate date range for summary tab
  const [transactionFilter, setTransactionFilter] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOrder, setSortOrder] = useState("newest");

  // Financial summary state - for all time
  const [allTimeIncome, setAllTimeIncome] = useState(0);
  const [allTimeExpenses, setAllTimeExpenses] = useState(0);

  // Infinite scroll
  const [displayedCount, setDisplayedCount] = useState(20);
  const [loadingMore, setLoadingMore] = useState(false);

  // Delete confirmation modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [transactionToDelete, setTransactionToDelete] = useState(null);

  // RTL support
  const isRTL = i18n.language === "ar";

  // Set document direction based on language
  useEffect(() => {
    document.documentElement.dir = isRTL ? "rtl" : "ltr";
    document.documentElement.lang = i18n.language;
  }, [isRTL, i18n.language]);

  const handleAddNewClick = (type) => {
    navigate("/transactions/new", {
      state: { type },
    });
  };

  const handleEditTransaction = (transaction) => {
    // The transaction object from API might have nested 'payment' property
    const paymentData = transaction.payment || transaction;
    const transactionId = paymentData.id;
    const transactionType = paymentData.type;

    // Check if it's a salary-related transaction
    const salaryTypes = ["salaryPayment", "salaryAdvance", "salaryDeduction"];

    if (salaryTypes.includes(transactionType)) {
      // For salary transactions, we need to find the employee ID
      // The transaction should have an employee property
      const employeeId =
        paymentData.employeeId ||
        transaction.employee?.id ||
        transaction.employeeId;

      if (!employeeId) {
        toast.error(
          t("Cannot edit salary transaction: Employee information is missing")
        );
        return;
      }

      // Navigate to AddTransactionPage for salary-related transactions
      navigate(`/employees/${employeeId}/transactions/${transactionId}/edit`, {
        state: {
          transactionToEdit: transaction, // Pass the full transaction object
          returnPath: "/payments", // Return to payments page after edit
        },
      });
    } else {
      // For regular transactions (income, expense, loans, etc.), use TransactionForm
      navigate(`/transactions/${transactionId}/edit`, {
        state: {
          transaction: paymentData, // Pass the payment object directly, not nested
        },
      });
    }
  };

  const handleDeleteTransaction = (transaction) => {
    // Show delete confirmation modal
    setTransactionToDelete(transaction);
    setShowDeleteModal(true);
  };

  const confirmDeleteTransaction = async () => {
    if (!transactionToDelete) return;

    try {
      const transactionId =
        transactionToDelete.id || transactionToDelete.payment?.id;
      await axios.delete(`api/payment/${transactionId}`, {
        headers: { Authorization: token },
      });
      toast.success(t("Transaction deleted successfully"));
      setShouldRefetch((prev) => !prev);
      setShowDeleteModal(false);
      setTransactionToDelete(null);
    } catch (error) {
      toast.error(t("Failed to delete transaction"));
      console.error("Delete error:", error);
      setShowDeleteModal(false);
    }
  };

  const cancelDeleteTransaction = () => {
    setShowDeleteModal(false);
    setTransactionToDelete(null);
  };

  const handleViewTransactionDetails = (transaction) => {
    // For now, just log - can implement modal or navigation later
    console.log("View transaction details:", transaction);
  };

  // Helper function to get transaction type - NO REMAPPING!
  const getTransactionType = (payment) => {
    // Use raw types directly from API, matching Android
    return payment.payment?.type || payment.type || "purchase";
  };

  // Calculate financial totals - matching Android logic exactly
  const calculateFinancialTotals = useCallback((payments) => {
    let netCashFlow = 0; // This is the main calculation matching Android
    let cashIn = 0,
      cashOut = 0; // Track total cash movements
    let income = 0,
      expenses = 0,
      takenLoans = 0,
      givenLoans = 0;
    let loanRepayments = 0,
      repaymentsReceived = 0;
    let salaryPayments = 0,
      salaryAdvances = 0,
      salaryDeductions = 0;
    let transfersIn = 0,
      transfersOut = 0;

    // Track types for debugging
    const typeCount = {};

    payments.forEach((payment) => {
      const amount = parseFloat(
        payment.payment?.amountPaid || payment.amountPaid || 0
      );
      const rawType = payment.payment?.type || payment.type || "purchase";

      // Count transaction types for debugging
      typeCount[rawType] = (typeCount[rawType] || 0) + 1;

      // CORRECT ACCOUNTING LOGIC (not Android's flawed logic)
      // OUTFLOWS (money leaving the company - negative):
      if (
        rawType === "purchase" || // Expenses/purchases
        rawType === "givenLoan" || // Lending money out
        rawType === "loanRepayment" || // Paying back borrowed money
        rawType === "salaryPayment" || // Paying employee salaries
        rawType === "salaryAdvance" || // Advancing money to employees
        rawType === "transferOut"
      ) {
        // Transferring money out
        netCashFlow += amount * -1; // Money going OUT (negative)
        cashOut += amount; // Track total cash out

        // INFLOWS (money entering/staying with company - positive):
      } else {
        // Includes: sale (revenue), takenLoan (borrowing), repaymentReceived,
        // transferIn, AND salaryDeduction (money withheld from salaries)
        netCashFlow += amount; // Money coming IN or SAVED (positive)
        cashIn += amount; // Track total cash in
      }

      // Now track individual categories for reporting
      switch (rawType) {
        case "sale": // INCOME type in Android
          income += amount;
          break;
        case "purchase": // EXPENSE type in Android
          expenses += amount;
          break;
        case "transferIn":
          transfersIn += amount;
          income += amount; // Count as income for totals
          break;
        case "transferOut":
          transfersOut += amount;
          expenses += amount; // Count as expense for totals
          break;
        case "takenLoan":
          takenLoans += amount;
          // Positive in cash flow but not counted as income
          break;
        case "givenLoan":
          givenLoans += amount;
          expenses += amount; // Count as expense
          break;
        case "loanRepayment":
          loanRepayments += amount;
          expenses += amount; // Count as expense
          break;
        case "repaymentReceived":
          repaymentsReceived += amount;
          income += amount; // Count as income
          break;
        case "salaryPayment":
          salaryPayments += amount;
          expenses += amount; // This IS an expense - money paid to employees
          break;
        case "salaryAdvance":
          salaryAdvances += amount;
          expenses += amount; // This IS an expense - money advanced to employees
          break;
        case "salaryDeduction":
          salaryDeductions += amount;
          // This is NOT an expense - it's money saved/withheld
          // It reduces the salary expense, not increases it
          break;
      }
    });

    // Debug log for transaction types
    console.log("Transaction types processed:", typeCount);
    console.log("Calculation breakdown:", {
      totalPayments: payments.length,
      netCashFlow,
      income,
      expenses,
      simpleNetProfit: income - expenses,
      takenLoans,
      salaryDeductions,
      formulaCheck: `${income} - ${expenses} + ${takenLoans} + ${salaryDeductions} = ${
        income - expenses + takenLoans + salaryDeductions
      }`,
      actualNetCashFlow: netCashFlow,
    });

    return {
      income,
      expenses,
      netProfit: income - expenses, // True profit/loss (operating)
      netCashFlow, // Actual cash position change
      cashIn, // Total cash inflows
      cashOut, // Total cash outflows
      operatingProfit: income - expenses, // Same as netProfit
      takenLoans,
      givenLoans,
      loansPayable: takenLoans - loanRepayments,
      loansReceivable: givenLoans - repaymentsReceived,
      salaryPayments,
      salaryAdvances,
      salaryDeductions,
      outstandingSalaries: salaryAdvances - salaryDeductions,
      transfersIn,
      transfersOut,
    };
  }, []);

  // Fetch payment data
  useEffect(() => {
    if (userData.Farm?.id) {
      setLoading(true);
      axios
        .get(`api/payment/findByFarmId/${userData.Farm?.id}`, {
          headers: {
            Authorization: `${token}`,
          },
        })
        .then((response) => {
          console.log("API Response:", response.data);
          const payments = response.data.items || response.data || [];
          console.log("Extracted payments:", payments);
          console.log("Number of payments:", payments.length);

          setPaymentsData(payments);

          // Calculate all-time financial totals
          const totals = calculateFinancialTotals(payments);
          setAllTimeIncome(totals.income);
          setAllTimeExpenses(totals.expenses);

          setLoading(false);
        })
        .catch((error) => {
          console.error("Error fetching data:", error);
          toast.error(t("Failed to load transactions"));
          setLoading(false);
        });
    }
  }, [userData.Farm?.id, shouldRefetch, token, calculateFinancialTotals, t]);

  // Format date
  const formatDate = (timestamp) => {
    if (!timestamp) return "";
    const date = new Date(timestamp);
    return new Intl.DateTimeFormat(isRTL ? "ar-EG" : "en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    }).format(date);
  };

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return "—";
    const isNegative = amount < 0;
    // Always use en-US locale to ensure dot (.) is used for decimals instead of comma (,)
    const formatted = new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    }).format(Math.abs(amount));
    return `${isNegative ? "-" : ""}${formatted} ${t("l.e")}`;
  };

  // Get period date range display
  const getPeriodDateRange = () => {
    const today = new Date();
    let startDate = new Date();

    switch (dateRange) {
      case "today":
        startDate = new Date(today);
        break;
      case "week":
        startDate.setDate(today.getDate() - 7);
        break;
      case "month":
        startDate.setMonth(today.getMonth() - 1);
        break;
      case "quarter":
        startDate.setMonth(today.getMonth() - 3);
        break;
      case "year":
        startDate.setFullYear(today.getFullYear() - 1);
        break;
      case "all":
        return t("All Time");
      default:
        startDate.setDate(today.getDate() - 7);
    }

    const formatDateForRange = (date) => {
      return new Intl.DateTimeFormat(isRTL ? "ar-EG" : "en-GB", {
        day: "numeric",
        month: "long",
        year: "numeric",
      }).format(date);
    };

    return `${t("From")} ${formatDateForRange(startDate)} ${t(
      "to"
    )} ${formatDateForRange(today)}`;
  };

  // Filter payments by date range
  const filterByDateRange = useCallback((payments, range) => {
    if (!payments || payments.length === 0) return [];
    if (range === "all") return payments;

    const today = new Date();
    today.setHours(23, 59, 59, 999);
    let startDate = new Date();

    switch (range) {
      case "today":
        startDate.setHours(0, 0, 0, 0);
        break;
      case "week":
        startDate.setDate(today.getDate() - 6);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "month":
        startDate = new Date(today.getFullYear(), today.getMonth(), 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "quarter":
        // Go back 3 months for quarter
        startDate.setMonth(today.getMonth() - 3);
        startDate.setHours(0, 0, 0, 0);
        break;
      case "year":
        startDate = new Date(today.getFullYear(), 0, 1);
        startDate.setHours(0, 0, 0, 0);
        break;
      default:
        return payments;
    }

    // Debug logging for date filtering
    console.log(`Filtering by ${range}:`, {
      startDate: startDate.toISOString(),
      endDate: today.toISOString(),
      totalPayments: payments.length,
    });

    const filtered = payments.filter((payment) => {
      const dateValue =
        payment.payment?.paymentDate ||
        payment.paymentDate ||
        payment.date ||
        payment.createTs ||
        payment.payment?.createTs;

      if (!dateValue) {
        console.warn("Payment without date - excluding from results:", payment);
        // Exclude payments without dates to match Android behavior
        return false;
      }

      const paymentDate = new Date(dateValue);

      // Check if date is valid
      if (isNaN(paymentDate.getTime())) {
        console.warn("Invalid date for payment:", dateValue, payment);
        return false;
      }

      return paymentDate >= startDate && paymentDate <= today;
    });

    console.log(`Filtered results: ${filtered.length} payments`);
    return filtered;
  }, []);

  // Filter by transaction type
  const filterByTransactionType = useCallback((payments, filter) => {
    if (!payments || filter === "all") return payments;

    return payments.filter((payment) => {
      const type = getTransactionType(payment);

      switch (filter) {
        // Basic types
        case "income":
          return type === "sale";
        case "expense":
          return type === "purchase";
        // Specific transaction types
        case "takenLoan":
          return type === "takenLoan";
        case "givenLoan":
          return type === "givenLoan";
        case "loanRepayment":
          return type === "loanRepayment";
        case "repaymentReceived":
          return type === "repaymentReceived";
        case "salaryPayment":
          return type === "salaryPayment";
        case "salaryAdvance":
          return type === "salaryAdvance";
        case "salaryDeduction":
          return type === "salaryDeduction";
        case "transferIn":
          return type === "transferIn";
        case "transferOut":
          return type === "transferOut";
        // Group filters
        case "loans":
          return (
            type === "takenLoan" ||
            type === "givenLoan" ||
            type === "loanRepayment" ||
            type === "repaymentReceived"
          );
        case "salary":
          return (
            type === "salaryAdvance" ||
            type === "salaryPayment" ||
            type === "salaryDeduction"
          );
        case "transfers":
          return type === "transferIn" || type === "transferOut";
        default:
          return true;
      }
    });
  }, []);

  // Search transactions
  const searchTransactions = useCallback((payments, query) => {
    if (!query.trim()) return payments;

    const queryLower = query.toLowerCase();
    return payments.filter((payment) => {
      const description = (
        payment.payment?.description ||
        payment.description ||
        ""
      ).toLowerCase();
      const category = (
        payment.payment?.category ||
        payment.category ||
        ""
      ).toLowerCase();
      const amount = String(
        payment.payment?.amountPaid || payment.amountPaid || 0
      );
      const invoiceNumber = String(payment.invoice?.number || "");

      return (
        description.includes(queryLower) ||
        category.includes(queryLower) ||
        amount.includes(queryLower) ||
        invoiceNumber.includes(queryLower)
      );
    });
  }, []);

  // Sort transactions
  const sortTransactions = useCallback((payments, order) => {
    return [...payments].sort((a, b) => {
      const dateA = new Date(
        a.payment?.paymentDate || a.paymentDate || a.date || a.createTs
      );
      const dateB = new Date(
        b.payment?.paymentDate || b.paymentDate || b.date || b.createTs
      );

      return order === "newest" ? dateB - dateA : dateA - dateB;
    });
  }, []);

  // Get processed transactions with all filters applied
  const processedTransactions = useMemo(() => {
    console.log("Processing transactions...");
    console.log("Initial paymentsData:", paymentsData.length, "transactions");

    let filtered = paymentsData;

    // Apply date range filter
    filtered = filterByDateRange(filtered, dateRange);
    console.log(
      "After date filter:",
      filtered.length,
      "transactions (range:",
      dateRange,
      ")"
    );

    // Apply transaction type filter
    filtered = filterByTransactionType(filtered, transactionFilter);
    console.log(
      "After type filter:",
      filtered.length,
      "transactions (filter:",
      transactionFilter,
      ")"
    );

    // Apply search
    filtered = searchTransactions(filtered, searchQuery);
    console.log("After search filter:", filtered.length, "transactions");

    // Apply sorting
    filtered = sortTransactions(filtered, sortOrder);

    console.log("Final processed transactions:", filtered.length);
    return filtered;
  }, [
    paymentsData,
    dateRange,
    transactionFilter,
    searchQuery,
    sortOrder,
    filterByDateRange,
    filterByTransactionType,
    searchTransactions,
    sortTransactions,
  ]);

  // Calculate filtered financial totals (for current date range)
  const filteredFinancialTotals = useMemo(() => {
    // Use summaryDateRange when in summary view, otherwise use regular dateRange
    const rangeToUse = activeView === "summary" ? summaryDateRange : dateRange;
    const dateFiltered = filterByDateRange(paymentsData, rangeToUse);
    const totals = calculateFinancialTotals(dateFiltered);

    // Debug logging for financial calculations
    console.log(`Financial totals for ${rangeToUse}:`, {
      filteredPayments: dateFiltered.length,
      income: totals.income,
      expenses: totals.expenses,
      netCashFlow: totals.netCashFlow,
      netProfit: totals.netProfit,
    });

    return totals;
  }, [
    paymentsData,
    dateRange,
    summaryDateRange,
    activeView,
    filterByDateRange,
    calculateFinancialTotals,
  ]);

  // Calculate overall loan balances (NOT filtered by date range)
  const overallLoanBalances = useMemo(() => {
    const totals = calculateFinancialTotals(paymentsData); // Use all data, no date filter
    return {
      loansPayable: totals.loansPayable,
      loansReceivable: totals.loansReceivable,
    };
  }, [paymentsData, calculateFinancialTotals]);

  // Displayed transactions for infinite scroll
  const displayedTransactions = useMemo(() => {
    return processedTransactions.slice(0, displayedCount);
  }, [processedTransactions, displayedCount]);

  // Handle infinite scroll
  const handleLoadMore = useCallback(() => {
    if (!loadingMore && displayedCount < processedTransactions.length) {
      setLoadingMore(true);
      setTimeout(() => {
        setDisplayedCount((prev) =>
          Math.min(prev + 20, processedTransactions.length)
        );
        setLoadingMore(false);
      }, 300);
    }
  }, [displayedCount, processedTransactions.length, loadingMore]);

  // Reset displayed count when filters change
  useEffect(() => {
    setDisplayedCount(20);
  }, [dateRange, transactionFilter, searchQuery, sortOrder]);

  return (
    <div className="expenses-sales-container">
      {/* Header Section */}
      <div className="expenses-header">
        <div className="header-left">
          <img src={payments} alt="Payments" className="header-icon" />
          <h1 className="header-title">{t("Expenses & Sales")}</h1>
        </div>

        <button
          className="add-new-payment-btn"
          onClick={() => navigate("/transactions/new")}
        >
          <span>+</span>
          {t("Add New Payment")}
        </button>
      </div>

      {/* View Tabs */}
      <div className="view-tabs">
        <button
          className={`view-tab ${activeView === "overview" ? "active" : ""}`}
          onClick={() => setActiveView("overview")}
        >
          <span>{t("Overview")}</span>
          <span className="tab-badge">{processedTransactions.length}</span>
        </button>
        <button
          className={`view-tab ${activeView === "summary" ? "active" : ""}`}
          onClick={() => setActiveView("summary")}
        >
          <span>{t("Financial Summary")}</span>
        </button>
      </div>

      {/* Render different views based on activeView */}
      {activeView === "overview" ? (
        <>
          {/* Date Range Selector */}
          <DateRangeSelector
            selectedRange={dateRange}
            onRangeChange={setDateRange}
          />

          {/* Summary Section - Matching Financial Summary Design */}
          <div className="overview-summary-container">
            {/* Period Date Range */}
            <div className="period-date-display">
              <span>{getPeriodDateRange()}</span>
            </div>

            <div className="overview-cards-section">
              <div className="section-label">{t("CASH FLOW STATEMENT")}</div>

              <div className="overview-cards-grid">
                <div className="overview-value-card">
                  <div className="value-title">{t("Net Cash Flow")}</div>
                  <div
                    className={`value-number ${
                      filteredFinancialTotals.netCashFlow >= 0
                        ? "profit"
                        : "loss"
                    }`}
                  >
                    {filteredFinancialTotals.netCashFlow >= 0 ? "+" : "-"}
                    {formatCurrency(
                      Math.abs(filteredFinancialTotals.netCashFlow)
                    )}
                  </div>
                  <div className="value-currency">{t("EGP")}</div>
                </div>

                <div className="overview-value-card double">
                  <div className="value-group">
                    <div className="value-title">{t("Cash In")}</div>
                    <div className="value-number positive">
                      +{formatCurrency(filteredFinancialTotals.cashIn)}
                    </div>
                    <div className="value-currency">{t("EGP")}</div>
                  </div>
                  <div className="value-group">
                    <div className="value-title">{t("Cash Out")}</div>
                    <div className="value-number negative">
                      -{formatCurrency(filteredFinancialTotals.cashOut)}
                    </div>
                    <div className="value-currency">{t("EGP")}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="filter-controls-container">
            <div className="filter-controls-wrapper">
              <div className="search-box-wrapper">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    className="search-input"
                    placeholder={t("Search transactions...")}
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                  />
                  {searchQuery && (
                    <button
                      className="search-clear"
                      onClick={() => setSearchQuery("")}
                    >
                      <FaTimes />
                    </button>
                  )}
                </div>
              </div>
              <div className="filter-controls">
                <Dropdown className="filter-dropdown">
                  <Dropdown.Toggle className="filter-dropdown-toggle">
                    <FaFilter className="me-1" />
                    {transactionFilter === "all"
                      ? t("All Transactions")
                      : transactionFilter === "income"
                      ? t("Income")
                      : transactionFilter === "expense"
                      ? t("Expenses")
                      : transactionFilter === "takenLoan"
                      ? t("Taken Loan")
                      : transactionFilter === "givenLoan"
                      ? t("Given Loan")
                      : transactionFilter === "loanRepayment"
                      ? t("Loan Repayment")
                      : transactionFilter === "repaymentReceived"
                      ? t("Repayment Received")
                      : transactionFilter === "salaryPayment"
                      ? t("Salary Payment")
                      : transactionFilter === "salaryAdvance"
                      ? t("Salary Advance")
                      : transactionFilter === "salaryDeduction"
                      ? t("Salary Deduction")
                      : transactionFilter === "transferIn"
                      ? t("Transfer In")
                      : transactionFilter === "transferOut"
                      ? t("Transfer Out")
                      : transactionFilter === "loans"
                      ? t("All Loans")
                      : transactionFilter === "salary"
                      ? t("All Salaries")
                      : transactionFilter === "transfers"
                      ? t("All Transfers")
                      : t("All Transactions")}
                  </Dropdown.Toggle>
                  <Dropdown.Menu className="filter-dropdown-menu">
                    <Dropdown.Item onClick={() => setTransactionFilter("all")}>
                      {t("All Transactions")}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Header>{t("Basic Types")}</Dropdown.Header>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("income")}
                    >
                      {t("Income")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("expense")}
                    >
                      {t("Expenses")}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Header>{t("Loans")}</Dropdown.Header>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("loans")}
                    >
                      {t("All Loans")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("takenLoan")}
                    >
                      {t("Taken Loan")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("givenLoan")}
                    >
                      {t("Given Loan")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("loanRepayment")}
                    >
                      {t("Loan Repayment")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("repaymentReceived")}
                    >
                      {t("Repayment Received")}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Header>{t("Salaries")}</Dropdown.Header>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("salary")}
                    >
                      {t("All Salaries")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("salaryPayment")}
                    >
                      {t("Salary Payment")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("salaryAdvance")}
                    >
                      {t("Salary Advance")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("salaryDeduction")}
                    >
                      {t("Salary Deduction")}
                    </Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Header>{t("Transfers")}</Dropdown.Header>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("transfers")}
                    >
                      {t("All Transfers")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("transferIn")}
                    >
                      {t("Transfer In")}
                    </Dropdown.Item>
                    <Dropdown.Item
                      onClick={() => setTransactionFilter("transferOut")}
                    >
                      {t("Transfer Out")}
                    </Dropdown.Item>
                  </Dropdown.Menu>
                </Dropdown>

                <button
                  className="sort-toggle-btn"
                  onClick={() =>
                    setSortOrder(sortOrder === "newest" ? "oldest" : "newest")
                  }
                  title={
                    sortOrder === "newest"
                      ? t("Newest First")
                      : t("Oldest First")
                  }
                >
                  {sortOrder === "newest" ? (
                    <>
                      <FaSortAmountDown className="me-1" /> {t("Newest First")}
                    </>
                  ) : (
                    <>
                      <FaSortAmountUp className="me-1" /> {t("Oldest First")}
                    </>
                  )}
                </button>
              </div>
            </div>
          </div>

          {/* Transactions List */}
          <h5
            style={{
              fontSize: "20px",
              fontWeight: "500",
              marginBottom: "16px",
              marginTop: "24px",
            }}
          >
            {t("Transactions")}
          </h5>

          {loading ? (
            <div style={{ textAlign: "center", padding: "40px" }}>
              <div className="spinner-border" role="status">
                <span className="visually-hidden">{t("Loading...")}</span>
              </div>
            </div>
          ) : processedTransactions.length > 0 ? (
            <>
              <div className="transactions-list">
                {displayedTransactions.map((transaction) => (
                  <TransactionCard
                    key={transaction.id || transaction.payment?.id}
                    transaction={transaction}
                    onEdit={handleEditTransaction}
                    onDelete={handleDeleteTransaction}
                    onViewDetails={handleViewTransactionDetails}
                    formatCurrency={formatCurrency}
                    formatDate={formatDate}
                  />
                ))}
              </div>

              {/* Load More Button for Infinite Scroll */}
              {displayedCount < processedTransactions.length && (
                <div className="text-center mt-4">
                  <button
                    className="btn btn-outline-primary"
                    onClick={handleLoadMore}
                    disabled={loadingMore}
                  >
                    {loadingMore ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm me-2"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        {t("Loading...")}
                      </>
                    ) : (
                      t("Load More")
                    )}
                  </button>
                  <p className="text-muted mt-2" style={{ fontSize: "14px" }}>
                    {t("Showing")} {displayedCount} {t("of")}{" "}
                    {processedTransactions.length} {t("transactions")}
                  </p>
                </div>
              )}
            </>
          ) : (
            <EmptyState
              onAddTransaction={() => handleAddNewClick("expense")}
              hasFilters={searchQuery || transactionFilter !== "all"}
            />
          )}
        </>
      ) : (
        /* Financial Summary View */
        <FinancialSummary
          totalIncome={filteredFinancialTotals.income}
          totalExpenses={filteredFinancialTotals.expenses}
          netProfit={filteredFinancialTotals.netProfit}
          takenLoans={filteredFinancialTotals.takenLoans}
          givenLoans={filteredFinancialTotals.givenLoans}
          loansPayable={overallLoanBalances.loansPayable} // Use overall, not filtered
          loansReceivable={overallLoanBalances.loansReceivable} // Use overall, not filtered
          formatCurrency={formatCurrency}
          currentPeriod={
            summaryDateRange === "today" ? "week" : summaryDateRange
          }
          onPeriodChange={(period) => {
            // Map period to summaryDateRange (separate from overview dateRange)
            if (period === "week") setSummaryDateRange("week");
            else if (period === "month") setSummaryDateRange("month");
            else if (period === "quarter") setSummaryDateRange("quarter");
            else if (period === "year") setSummaryDateRange("year");
          }}
        />
      )}

      {/* Delete Confirmation Modal */}
      <DeleteTransactionModal
        isOpen={showDeleteModal}
        onClose={cancelDeleteTransaction}
        onConfirm={confirmDeleteTransaction}
        transaction={transactionToDelete}
      />
    </div>
  );
};

export default Payments;
